import { redirect } from "@sveltejs/kit";
import auth from "../../../../chunks/auth.js";
import { p as prisma } from "../../../../chunks/prisma.js";
import { g as getActivePromotions, b as buildPromotionIndex, a as selectBestPromotionIndexed } from "../../../../chunks/promotionPricing.js";
const DEFAULT_ITEMS_PER_PAGE = 10;
const MAX_ITEMS_PER_PAGE = 1e3;
const load = (async ({ locals: { user }, url }) => {
  if (await auth.isAdmin(user)) return redirect(303, "/admin/product");
  try {
    const page = Math.max(1, parseInt(url.searchParams.get("page") || "1"));
    const categoriesParam = url.searchParams.get("categories") || "";
    const selectedCategories = categoriesParam ? categoriesParam.split(",").map((id) => parseInt(id)).filter((id) => !isNaN(id)) : [];
    const year = url.searchParams.get("year") || "all";
    const search = url.searchParams.get("search") || "";
    const limit = Math.min(
      MAX_ITEMS_PER_PAGE,
      Math.max(1, parseInt(url.searchParams.get("limit") || DEFAULT_ITEMS_PER_PAGE.toString()))
    );
    const sort = url.searchParams.get("sort") || "publication_desc";
    const skip = (page - 1) * limit;
    const getOrderBy = (sortParam) => {
      switch (sortParam) {
        case "price_asc":
          return [{ price: "asc" }];
        case "price_desc":
          return [{ price: "desc" }];
        case "name_asc":
          return [{ name: "asc" }];
        case "publication_asc":
          return [{ publicationDate: "asc" }];
        case "publication_desc":
          return [{ publicationDate: "desc" }];
        case "newest":
        default:
          return [{ publicationDate: "desc" }, { name: "asc" }];
      }
    };
    let where = { isPublished: true };
    if (selectedCategories.length > 0) {
      where = {
        ...where,
        categories: { some: { id: { in: selectedCategories } } }
      };
    }
    if (search) {
      where = {
        ...where,
        OR: [
          { name: { contains: search, mode: "insensitive" } },
          { description: { contains: search, mode: "insensitive" } },
          { citation: { contains: search, mode: "insensitive" } }
        ]
      };
    }
    if (year !== "all") {
      const yearInt = parseInt(year);
      where = {
        ...where,
        publicationDate: {
          gte: /* @__PURE__ */ new Date(`${yearInt}-01-01`),
          lt: /* @__PURE__ */ new Date(`${yearInt + 1}-01-01`)
        }
      };
    }
    let products, total;
    if (selectedCategories.includes(16) && selectedCategories.length === 1) {
      const orderBy = sort === "newest" || sort === "publication_desc" ? [{ sortOrder: "asc" }, { publicationDate: "desc" }] : sort === "publication_asc" ? [{ sortOrder: "asc" }, { publicationDate: "asc" }] : getOrderBy(sort);
      [products, total] = await Promise.all([
        prisma.product.findMany({
          where,
          include: {
            Image: { select: { url: true } },
            categories: true
          },
          orderBy,
          skip,
          take: limit
        }),
        prisma.product.count({ where })
      ]);
    } else {
      [products, total] = await Promise.all([
        prisma.product.findMany({
          where,
          include: {
            Image: { select: { url: true } },
            categories: true
          },
          orderBy: getOrderBy(sort),
          skip,
          take: limit
        }),
        prisma.product.count({ where })
      ]);
    }
    const productFilterForCounts = {
      isPublished: true
    };
    if (year !== "all") {
      const yearInt = parseInt(year);
      productFilterForCounts.publicationDate = {
        gte: /* @__PURE__ */ new Date(`${yearInt}-01-01`),
        lt: /* @__PURE__ */ new Date(`${yearInt + 1}-01-01`)
      };
    }
    if (search) {
      productFilterForCounts.OR = [
        { name: { contains: search, mode: "insensitive" } },
        { description: { contains: search, mode: "insensitive" } },
        { citation: { contains: search, mode: "insensitive" } }
      ];
    }
    const categoriesWithCounts = await prisma.category.findMany({
      orderBy: { sortOrder: "asc" },
      include: {
        _count: {
          select: {
            Products: { where: productFilterForCounts }
          }
        }
      }
    });
    const categoryCounts = categoriesWithCounts.map(({ _count, ...category }) => ({
      ...category,
      count: _count.Products
    }));
    const allCategoryIds = Array.from(new Set(products.flatMap((p) => p.categories.map((c) => c.id))));
    const activePromotions = await getActivePromotions(/* @__PURE__ */ new Date(), {
      productIds: products.map((p) => p.id),
      categoryIds: allCategoryIds
    });
    const promoIndex = buildPromotionIndex(activePromotions);
    const productsWithPricing = products.map((p) => {
      const categoryIds = p.categories.map((c) => c.id);
      const applied = selectBestPromotionIndexed(p.price, p.id, categoryIds, promoIndex);
      return applied ? {
        ...p,
        discountedPrice: applied.finalUnitPrice,
        appliedPromotion: {
          id: applied.promotionId,
          name: applied.name,
          discountType: applied.discountType,
          discountValue: applied.discountValue
        }
      } : p;
    });
    const totalPages = Math.ceil(total / limit);
    if (page > totalPages && total > 0) {
      const categoriesQuery = selectedCategories.length > 0 ? selectedCategories.join(",") : "";
      const searchQuery = search ? encodeURIComponent(search) : "";
      return redirect(
        302,
        `?page=${totalPages}&categories=${categoriesQuery}&year=${year}&limit=${limit}&search=${searchQuery}`
      );
    }
    return {
      // Cast so page component can access discountedPrice; original price retained in price field
      products: productsWithPricing,
      categories: categoryCounts,
      page,
      totalPages,
      totalResults: total,
      selectedCategories
    };
  } catch (e) {
    console.error("getPublishedProducts: ", e);
    return { status: 500, error: "Internal server error getting published products" };
  }
});
export {
  load
};
